import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Color, Size, TILE_GAP_PX, TILE_ROW_UNIT, blockHeightFor, columnsFor, layoutTiles, maxHeightFor, tileRowsFor, t, tileEditReducer, type CanvasOptions, type TileBounds, type TileEditEvent, type TilePlacement, type TileSpan } from '@bms2/shared';
import { Button } from '../controls';
import { Icon } from '../Icon';
import { DragList } from './DragList';
import styles from './Tile.module.css';

/**
 * Tile Canvas (piece 4): the responsive container under every Tile grid. One
 * block equals one column width and serves both dimensions; a Tile spans 1,
 * 2 or 3 columns and is a whole number of blocks tall; order survives every
 * restack; three columns is a ceiling, not a target; a Tile's minWidth can
 * force a column to drop; below the collapse point every Tile is full width.
 * The canvas assigns width and position and nothing else, and restacks only
 * in explicit edit mode, through the one drag-and-drop list. 🔨 At two
 * columns and three a Tile's content is measured and rounded up to the row
 * unit (decision 0321), and each column is its own stack (decision 1080): a
 * Tile stands under the one above it in its column, and stretches down to
 * where the next Tile in its column starts or to the canvas's foot, so no row
 * alignment leaves a void beside a taller neighbour.
 */
export const tileCanvasDocumentedStates = ['three', 'two', 'one', 'edit'] as const;

export interface CanvasTile {
  placement: TilePlacement;
  bounds: TileBounds;
  title: string;
  render: () => ReactNode;
  /** 🔒 An empty record-page tile drawn as its title row alone: it keeps its place and takes no stretch (decision 6700). */
  slim?: boolean;
}

export interface TileCanvasProps {
  tiles: readonly CanvasTile[];
  options: CanvasOptions;
  label: string;
  /** Explicit edit mode: the drag list mounts, the padlocks show, and only then does anything restack. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** The arrangement after a move, for the per-user store. */
  onArrange?: (tiles: TilePlacement[]) => void;
  /** A measured width for the suite and the harness; the canvas measures itself otherwise. */
  widthOverride?: number;
}

export function TileCanvas({ tiles, options, label, editing = false, onEditingChange, onArrange, widthOverride }: TileCanvasProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  const width = widthOverride ?? measuredWidth;
  const observers = useRef(new Map<string, ResizeObserver>());
  useLayoutEffect(() => {
    const held = observers.current;
    return () => held.forEach((observer) => observer.disconnect());
  }, []);

  useLayoutEffect(() => {
    if (widthOverride !== undefined) return;
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // One measurement per frame, and only when the width really moved: a one-pixel flutter relaid the canvas on every tick (product owner, 2026-09-10).
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setMeasuredWidth((previous) => {
        const next = el.clientWidth || null;
        return previous !== null && next !== null && Math.abs(previous - next) < 2 ? previous : next;
      }));
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [widthOverride]);

  // 🔨 The gutter is the token's own number (decision 0601); the grid below draws the token itself.
  const gap = options.gap ?? TILE_GAP_PX;
  const columns = columnsFor(width ?? Number.POSITIVE_INFINITY, tiles, { ...options, gap });
  const block = width === null ? null : (width - gap * (columns - 1)) / columns;
  const blockPx = block === null ? null : blockHeightFor(block, options);
  // Each slot's own height in pixels, observed; a slot not yet measured is laid out at its declared blocks.
  const [heights, setHeights] = useState<Record<string, number>>({});
  const rows = blockPx === null || columns === 1 ? undefined : Object.fromEntries(
    tiles.map((tile) => {
      const px = heights[tile.placement.id];
      return [tile.placement.id, tileRowsFor(px ?? maxHeightFor(tile.placement.height, blockPx, gap), TILE_ROW_UNIT, gap)];
    }),
  );
  const placed = layoutTiles(
    tiles.map((tile) => tile.placement),
    columns,
    rows,
    new Set(tiles.filter((tile) => tile.slim === true).map((tile) => tile.placement.id)),
  );
  const observe = (id: string) => (el: HTMLDivElement | null) => {
    const previous = observers.current.get(id);
    if (previous) {
      previous.disconnect();
      observers.current.delete(id);
    }
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      // 🔨 The content's own height, read with the stretch lifted for the one reading (decision 1080): a stretched slot measured as it stands would never shrink back.
      const stretch = el.style.minHeight;
      el.style.minHeight = '';
      const next = Math.round(el.getBoundingClientRect().height);
      el.style.minHeight = stretch;
      setHeights((was) => (was[id] === next ? was : { ...was, [id]: next }));
    });
    observer.observe(el);
    observers.current.set(id, observer);
  };
  const gridStyle = {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    // At one column every row is its Tile's content height (decision 0287); at two or three the row is the unit a measured Tile rounds up to (decision 0321).
    gridAutoRows: blockPx === null || columns === 1 ? 'auto' : `${TILE_ROW_UNIT}px`,
    gap: 'var(--tile-gap)',
  } as CSSProperties;

  const apply = (event: TileEditEvent) => {
    const next = tileEditReducer({ editing, tiles: tiles.map((tile) => tile.placement) }, event);
    onArrange?.(next.tiles);
  };
  const reorder = (id: string, toIndex: number) => apply({ type: 'move', id, toIndex });
  const spanLabel = (n: number) => `${n} ${t('tile.columnsOf')}`;

  return (
    <div className={styles.canvasWrap} data-tile-canvas="true" data-columns={columns} data-editing={editing ? 'true' : 'false'}>
      {onEditingChange ? (
        <div className={styles.canvasBar}>
          <Button size={Size.Small} color={editing ? Color.Navy : Color.Inactive} text={[t(editing ? 'tile.done' : 'tile.edit')]} actions={[() => onEditingChange(!editing)]} />
        </div>
      ) : null}
      {editing ? (
        <div className={styles.arrange} data-tile-arrange="true">
          <DragList
            label={t('tile.arrange')}
            items={tiles.map((tile) => ({ id: tile.placement.id, label: tile.title, fixed: tile.placement.locked }))}
            onReorder={(_items, move) => reorder(move.id, move.to)}
          />
        </div>
      ) : null}
      <div ref={ref} className={styles.canvas} style={gridStyle} aria-label={label} role="list">
        {placed.map((p) => {
          const tile = tiles.find((candidate) => candidate.placement.id === p.id)!;
          const sized = blockPx !== null && columns > 1;
          // 🔒 A Tile shorter than the room its column has before the next Tile starts stretches to fill it (decision 1080).
          const stretched = sized && p.stand > p.height;
          const slotStyle = {
            gridColumn: `${p.column + 1} / span ${p.span}`,
            gridRow: `${p.row + 1} / span ${p.stand}`,
            ...(sized ? { '--tile-max-height': `${maxHeightFor(tile.placement.height, blockPx, gap)}px` } : {}),
            // Every sized slot fills the rows it stands in, so two Tiles ending on one row unit end level.
            ...(sized ? { minHeight: `${p.stand * TILE_ROW_UNIT + (p.stand - 1) * gap}px` } : {}),
          } as CSSProperties;
          return (
            <div
              key={p.id}
              ref={sized ? observe(p.id) : undefined}
              className={styles.slot}
              style={slotStyle}
              role="listitem"
              data-tile-slot={p.id}
              data-span={p.span}
              data-height={columns === 1 ? 1 : tile.placement.height}
              data-rows={sized ? p.height : undefined}
              data-stand={sized ? p.stand : undefined}
              data-stretched={stretched ? 'true' : undefined}
              data-slim={tile.slim ? 'true' : undefined}
              data-sized={sized ? 'content' : 'row'}
              data-locked={tile.placement.locked ? 'true' : 'false'}
            >
              {editing ? (
                <div className={styles.editBar} data-tile-controls={p.id}>
                  <span className={styles.padlock} data-padlock={tile.placement.locked ? 'locked' : 'unlocked'}>
                    <Icon meaning={tile.placement.locked ? 'locked' : 'unlocked'} size="inline" label={t(tile.placement.locked ? 'tile.locked' : 'tile.unlocked')} />
                  </span>
                  {tile.placement.locked ? null : (
                    <>
                      <span className={styles.editGroup} role="group" aria-label={t('tile.columns')}>
                        {([1, 2, 3] as TileSpan[]).map((n) => (
                          <button
                            key={n}
                            type="button"
                            className={styles.editButton}
                            aria-pressed={tile.placement.span === n}
                            aria-label={spanLabel(n)}
                            data-span-button={n}
                            onClick={() => apply({ type: 'resize', id: p.id, span: n })}
                          >
                            {n}
                          </button>
                        ))}
                      </span>
                      <span className={styles.editGroup} role="group" aria-label={t('tile.rows')}>
                        <button type="button" className={styles.editButton} aria-label={t('tile.shorter')} disabled={tile.placement.height <= 1} data-height-button="shorter" onClick={() => apply({ type: 'resize', id: p.id, height: tile.placement.height - 1 })}>
                          <Icon meaning="indeterminate" size="inline" />
                        </button>
                        <span className={styles.editValue} data-height-value={tile.placement.height}>{tile.placement.height}</span>
                        <button type="button" className={styles.editButton} aria-label={t('tile.taller')} data-height-button="taller" onClick={() => apply({ type: 'resize', id: p.id, height: tile.placement.height + 1 })}>
                          <Icon meaning="add" size="inline" />
                        </button>
                      </span>
                      <button type="button" className={`${styles.editButton} ${styles.editRemove}`} aria-label={t('tile.remove')} data-remove-tile={p.id} onClick={() => apply({ type: 'remove', id: p.id })}>
                        <Icon meaning="remove" size="inline" />
                      </button>
                    </>
                  )}
                </div>
              ) : null}
              {tile.render()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
